import { ElementBuilder } from "@universityofmaryland/web-builder-library";
import { person } from "@universityofmaryland/web-elements-library/composite";
import { LoadingState } from "../../states/loading.js";
import "@universityofmaryland/web-elements-library";
import "@universityofmaryland/web-styles-library";
import "@universityofmaryland/web-utilities-library/theme";
import { Announcer } from "../../states/announcer.js";
import { expertsFetchStrategy } from "../../strategies/fetch/experts.js";
import { mapExpertToBioProps, buildFullName } from "../../strategies/display/experts.js";
import { setShadowStyles } from "../../helpers/styles/shadow.js";
const createFetchProps = (token, expertId) => ({
  token,
  limit: 1,
  offset: 0,
  ids: [expertId]
});
const createSuccessAnnouncer = (expert) => {
  const fullName = buildFullName(expert);
  const message = `Loaded profile for ${fullName}`;
  return new Announcer({ message });
};
class BioFeedState {
  /**
   * Initialize state with initial styles
   *
   * @param initialStyles - Initial CSS styles
   */
  constructor(initialStyles) {
    this.stylesArray = [];
    this.shadowRoot = null;
    this.stylesArray.push(initialStyles);
  }
  /**
   * Add styles to the accumulated styles
   *
   * @param styles - CSS styles to add
   */
  addStyles(styles) {
    this.stylesArray.push(styles);
  }
  /**
   * Set shadow root reference for style updates
   *
   * @param shadow - Shadow root element
   */
  setShadowRoot(shadow) {
    this.shadowRoot = shadow;
  }
  /**
   * Update shadow DOM styles
   *
   * @returns Promise that resolves when styles are updated
   */
  async updateShadowStyles() {
    if (!this.shadowRoot) return;
    await setShadowStyles({
      shadowRoot: this.shadowRoot,
      styles: this.getStyles()
    });
  }
  /**
   * Get accumulated styles as single string
   *
   * @returns Combined CSS styles
   */
  getStyles() {
    return this.stylesArray.join("\n");
  }
  /**
   * Get shadow root callback for events
   *
   * @returns Callback function for shadow root
   */
  getShadowCallback() {
    return (shadow) => this.setShadowRoot(shadow);
  }
}
const renderSuccess = async (container, expert, state, isThemeDark) => {
  const bioProps = mapExpertToBioProps(expert, "small", isThemeDark);
  const bioElement = person.bio.small(bioProps);
  const announcer = createSuccessAnnouncer(expert);
  const children = [bioElement.element, announcer.getElement()];
  children.forEach((child) => container.appendChild(child));
  state.addStyles(bioElement.styles);
  await state.updateShadowStyles();
};
const logError = (errorType, expertId, error) => {
  switch (errorType) {
    case "not_found":
      console.warn(
        `[Expert Bio Feed] No expert found with ID "${expertId}". Please verify the expert ID is correct and the expert exists in the system.`
      );
      break;
    case "graphql_error":
      console.warn(
        `[Expert Bio Feed] GraphQL error occurred while fetching expert "${expertId}". Check network connection and API availability.`,
        error
      );
      break;
    case "invalid_request":
      console.warn(
        `[Expert Bio Feed] Invalid request for expert "${expertId}". Ensure both data-token and data-id attributes are provided.`
      );
      break;
  }
};
const expertsBio = (props) => {
  const { token, expertId, isThemeDark = false } = props;
  const containerBuilder = new ElementBuilder("div").withClassName(
    "expert-bio-feed"
  );
  const container = containerBuilder.getElement();
  if (!expertId) {
    logError("invalid_request", "unknown");
    const model2 = containerBuilder.build();
    return {
      element: model2.element,
      styles: "",
      events: {}
    };
  }
  const validExpertId = expertId;
  const loading = new LoadingState({ isThemeDark });
  const state = new BioFeedState(loading.styles);
  const initialize = async () => {
    loading.show(container);
    try {
      const fetchProps = createFetchProps(token, validExpertId);
      const variables = expertsFetchStrategy.composeApiVariables(fetchProps);
      const entries = await expertsFetchStrategy.fetchEntries(variables);
      loading.hide();
      if (!entries || entries.length === 0) {
        logError("not_found", validExpertId);
        return;
      }
      await renderSuccess(container, entries[0], state, isThemeDark);
    } catch (error) {
      loading.hide();
      logError("graphql_error", validExpertId, error);
    }
  };
  initialize();
  const model = containerBuilder.build();
  return {
    element: model.element,
    get styles() {
      return state.getStyles();
    },
    events: {
      callback: state.getShadowCallback()
    }
  };
};
export {
  expertsBio
};
//# sourceMappingURL=bio.js.map
